import { useCallback } from 'react';
import { mod } from 'react-swipeable-views-core';
import type { Collection, List, Normalized } from 'lib/LobbyEntities';
import amplitude from 'lib/analytics';

const getItemRef = (accessRef: string) => {
  const parts = accessRef.split('_');
  return parts.length ? parts[parts.length - 1] : accessRef;
};

type useOffersCarouselAnalyticsProps = {
  list: List;
  collection: Normalized<Collection>;
};

/**
 * Custom hook to track slide changes in the offers carousel.
 * This is an experimental implementation to gather data on carousel interactions.
 * The tracked data includes previous and current slide indexes and references.
 *
 * Note: This is a temporary solution and the eventual use of this data is still under consideration.
 */
const useOffersCarouselAnalytics = ({ list, collection }: useOffersCarouselAnalyticsProps) =>
  useCallback(
    ({ prevIndex, newIndex }: { prevIndex: number; newIndex: number }) => {
      const modPrevIndex = mod(prevIndex, collection.totalElements);
      const modNewIndex = mod(newIndex, collection.totalElements);
      const prevItem = list.children[modPrevIndex];
      const currentItem = list.children[modNewIndex];

      amplitude.track('Offer Carousel Slide Changed', {
        'Section ID': collection.refName,
        'Section Name': collection.title,
        'Previous Slide Position': modPrevIndex + 1,
        'Current Slide Position': modNewIndex + 1,
        'Previous Offer ID': getItemRef(prevItem.__access),
        'Current Offer ID': getItemRef(currentItem.__access),
        'Total Elements': collection.totalElements
      });
    },
    [collection, list]
  );

export default useOffersCarouselAnalytics;
